import React, { useEffect, useMemo, useState } from "react";
import { api, speak } from "@/lib/kielo";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Volume2, Check, X, RefreshCw, ArrowRight } from "lucide-react";

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function SentenceBuilder() {
  const { t, lang } = useI18n();
  const [sentences, setSentences] = useState([]);
  const [index, setIndex] = useState(0);
  const [bank, setBank] = useState([]); // {w, id}
  const [answer, setAnswer] = useState([]);
  const [result, setResult] = useState(null); // "correct" | "wrong"
  const [score, setScore] = useState(0);

  useEffect(() => {
    api.get("/sentences", { params: { lang } }).then(({ data }) => setSentences(shuffle(data)));
  }, [lang]);

  const current = sentences[index];

  useEffect(() => {
    if (current) {
      setBank(shuffle(current.words.map((w, i) => ({ w, id: `${i}-${w}` }))));
      setAnswer([]);
      setResult(null);
    }
  }, [current]);

  if (!sentences.length) return <div className="max-w-2xl mx-auto px-4 py-20 text-[#52635B]">{t("common.loading")}</div>;

  const addWord = (tok) => {
    if (result) return;
    setAnswer((a) => [...a, tok]);
    setBank((b) => b.filter((x) => x.id !== tok.id));
  };
  const removeWord = (tok) => {
    if (result) return;
    setAnswer((a) => a.filter((x) => x.id !== tok.id));
    setBank((b) => [...b, tok]);
  };
  const check = () => {
    const built = answer.map((x) => x.w).join(" ").toLowerCase();
    const target = current.words.join(" ").toLowerCase();
    const ok = built === target;
    setResult(ok ? "correct" : "wrong");
    if (ok) { setScore((s) => s + 1); speak(current.fi); }
  };
  const clear = () => { if (!result) { setBank((b) => shuffle([...b, ...answer])); setAnswer([]); } };
  const next = () => {
    if (index + 1 < sentences.length) setIndex(index + 1);
    else { setIndex(0); setSentences(shuffle(sentences)); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 md:py-14" data-testid="sentence-builder">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-2xl sm:text-3xl font-semibold text-[#0E382B]">{t("hub.build")}</h1>
        <span className="text-sm font-medium text-[#52635B]">{t("game.score")}: {score}</span>
      </div>

      <div className="rounded-3xl bg-white border border-[#E9E6DF] p-6 sm:p-8">
        <div className="flex items-center gap-2 text-[#52635B] text-sm">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#8A5A1E] bg-[#F6ECDD] px-2 py-0.5 rounded-full">{current.level}</span>
          {t("game.tapWords")}
        </div>
        <p className="mt-3 text-[#0E382B] font-medium text-lg italic">“{current.en}”</p>

        {/* answer area */}
        <div className="mt-5 min-h-[64px] rounded-2xl border-2 border-dashed border-[#E2DFD7] p-3 flex flex-wrap gap-2" data-testid="builder-answer">
          {answer.map((tok) => (
            <button key={tok.id} onClick={() => removeWord(tok)} className="px-3 py-2 rounded-xl bg-[#0E382B] text-white font-medium">
              {tok.w}
            </button>
          ))}
        </div>

        {/* word bank */}
        <div className="mt-4 flex flex-wrap gap-2">
          {bank.map((tok) => (
            <button key={tok.id} data-testid="builder-bank-word" onClick={() => addWord(tok)} className="px-3 py-2 rounded-xl bg-[#F1EFEB] text-[#0E382B] font-medium border border-[#E2DFD7] hover:border-[#0E382B] transition-colors">
              {tok.w}
            </button>
          ))}
        </div>

        {result && (
          <div className={`mt-5 rounded-2xl px-4 py-3 flex items-center gap-2 font-medium ${result === "correct" ? "bg-[#E4F0E9] text-[#0E5A35]" : "bg-[#FBE3E3] text-[#7A2E2E]"}`} data-testid="builder-feedback">
            {result === "correct" ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
            {result === "correct" ? t("game.correct") : `${t("game.wrong")} ${current.fi}`}
            <button onClick={() => speak(current.fi)} className="ml-auto text-[#1E429F]"><Volume2 className="w-5 h-5" /></button>
          </div>
        )}

        <div className="mt-6 flex gap-3">
          {!result ? (
            <>
              <Button onClick={check} disabled={!answer.length} data-testid="builder-check" className="rounded-full h-11 px-6 bg-[#E46A28] hover:bg-[#c9591f] text-white">{t("game.check")}</Button>
              <Button onClick={clear} variant="outline" data-testid="builder-clear" className="rounded-full h-11 px-5 border-[#E2DFD7] text-[#52635B]"><RefreshCw className="w-4 h-4 mr-1.5" />{t("game.clear")}</Button>
            </>
          ) : (
            <Button onClick={next} data-testid="builder-next" className="rounded-full h-11 px-6 bg-[#0E382B] hover:bg-[#1C5442] text-white">{t("game.next")} <ArrowRight className="w-4 h-4 ml-1.5" /></Button>
          )}
        </div>
      </div>
    </div>
  );
}
